import { z } from 'zod';
import { UsageSchema } from './account';
import { CREDIT_RATES } from './credits';
import { PassageContextSchema } from './rewrite';
import { MAX_TEXT_LENGTH, countCharacters } from './text';

/**
 * The AI tools everyone has, which format the text in one go: unlike a rewrite, a tool returns
 * one version, laid out the way it says, with the writer's own words wherever it can keep them.
 * Menus list them in this order: layouts, then summaries, then clean-ups. All start visible.
 */
export const BUILT_IN_TOOLS = [
  'bullets',
  'numbered',
  'checklist',
  'gherkin',
  'summary',
  'line-breaks',
  'simplify',
  'plain',
] as const;

export type BuiltInTool = (typeof BUILT_IN_TOOLS)[number];

/** English names, for the API's documentation and the mock provider. */
export const BUILT_IN_TOOL_LABELS: Readonly<Record<BuiltInTool, string>> = {
  bullets: 'Bullet points',
  numbered: 'Numbered steps',
  checklist: 'Checklist',
  gherkin: 'Gherkin scenarios',
  summary: 'Summary',
  'line-breaks': 'Fix line breaks',
  simplify: 'Plain language',
  plain: 'Plain text',
};

export const isBuiltInTool = (tool: string): tool is BuiltInTool =>
  (BUILT_IN_TOOLS as readonly string[]).includes(tool);

/** Most AI tools of their own an account can have. */
export const MAX_AI_TOOLS = 20;
export const MAX_AI_TOOL_NAME_LENGTH = 40;
export const MAX_AI_TOOL_INSTRUCTION_LENGTH = 500;

/** A tool a writer makes: what it's called, and what it does to the text. */
export const AiToolInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(MAX_AI_TOOL_NAME_LENGTH)
      .describe('What the tool is called, as the AI tools menu lists it.'),
    instruction: z
      .string()
      .trim()
      .min(1)
      .max(MAX_AI_TOOL_INSTRUCTION_LENGTH)
      .describe(
        'What the tool does to the text, in the writer’s words, such as “lay it out as meeting notes”.',
      ),
  })
  .meta({ id: 'AiToolInput' });

export type AiToolInput = z.infer<typeof AiToolInputSchema>;

export const AiToolSchema = AiToolInputSchema.extend({ id: z.string() }).meta({ id: 'AiTool' });

export type AiTool = z.infer<typeof AiToolSchema>;

export const AiToolResponseSchema = z.object({ tool: AiToolSchema }).meta({ id: 'AiToolResponse' });

export const AiToolsResponseSchema = z
  .object({ tools: z.array(AiToolSchema).describe('Oldest first.') })
  .meta({ id: 'AiTools' });

const formattedLimit = MAX_TEXT_LENGTH.toLocaleString('en-US');

export const ToolRunRequestSchema = z
  .object({
    text: z
      .string()
      .refine((text) => text.trim().length > 0, 'Text must not be empty.')
      .refine(
        (text) => countCharacters(text) <= MAX_TEXT_LENGTH,
        `Text must be at most ${formattedLimit} characters.`,
      )
      .describe(`The text to format: up to ${formattedLimit} characters, in any language.`),
    tool: z
      .string()
      .min(1)
      .max(64)
      .optional()
      .describe(
        `A built-in tool (${BUILT_IN_TOOLS.join(', ')}), or the id of one of the caller’s own tools, which requires signing in, or an API key. Each run uses ${String(CREDIT_RATES.toolPerWord)} credits per word of the text.`,
      ),
    instruction: z
      .string()
      .trim()
      .min(1)
      .max(MAX_AI_TOOL_INSTRUCTION_LENGTH)
      .optional()
      .describe(
        'In place of `tool`: what to do to the text, in the writer’s words, such as “a list of pros and cons”, followed once like a tool of their own, without making one. Needs no account, and uses the same credits.',
      ),
    context: PassageContextSchema.optional(),
  })
  .refine(
    ({ tool, instruction }) => (tool === undefined) !== (instruction === undefined),
    'Send either a tool or an instruction.',
  )
  .meta({ id: 'ToolRunRequest' });

export type ToolRunRequest = z.infer<typeof ToolRunRequestSchema>;

export const ToolRunResponseSchema = z
  .object({
    text: z.string().describe('The text, formatted by the tool.'),
    usage: UsageSchema.describe('Daily credit usage after this run.'),
  })
  .meta({ id: 'ToolRunResponse' });

export type ToolRunResponse = z.infer<typeof ToolRunResponseSchema>;
